import { runCloudFunction } from '@/store/ParseUtils'
import type { Study } from '@pocketprep/types'
import { stripeModule } from '@/store/stripe/module'
import { fetchLoadable, resetLoadable } from '@/loadingUtils'
import { type Stripe as StripeNode } from 'stripe'
import { loadStripe, type Stripe, type StripeElements } from '@stripe/stripe-js'
import { instructorModule } from '@/store/instructor/module'

type TExams = {
    [examGuid: string]: number
}

/** CREATE PAYMENT INTENT **/
type TCreatePurchaseSeatsPaymentIntent = (params: {
    exams: TExams
    orgInstructorId: string
    saveCardForLater: boolean
    customerContactInfo?: {
        name?: string
        city?: string
        state?: string
        zip?: string
        country?: string
    }
}) => {
    clientSecret: string
    paymentIntentId: string
}

type TCreateCheckoutSession = (params: {
    mode: StripeNode.Checkout.SessionCreateParams.Mode
    orgInstructorId: string
    priceData?: StripeNode.Checkout.SessionCreateParams.LineItem.PriceData
    currency?: StripeNode.Checkout.SessionCreateParams['currency']
    paymentIntentMetadata?: StripeNode.Checkout.SessionCreateParams['metadata']
}) => Promise<{
    clientSecret: string | null
    checkoutSessionId: string
}>

const createCheckoutSession = async ({ mode, orgInstructorId, priceData, paymentIntentMetadata }: {
    mode: 'payment' | 'setup'
    orgInstructorId: string
    priceData?: StripeNode.Checkout.SessionCreateParams.LineItem.PriceData
    paymentIntentMetadata?: StripeNode.Checkout.SessionCreateParams['metadata']
}) => {
    const {
        clientSecret,
        checkoutSessionId,
    } = await runCloudFunction<TCreateCheckoutSession>('createCheckoutSession', {
        mode,
        orgInstructorId,
        priceData,
        currency: 'usd',
        paymentIntentMetadata,
    })

    return {
        clientSecret,
        checkoutSessionId,
    }
}

type TFetchCheckoutSession = (params: {
    checkoutSessionId: string
}) => Promise<StripeNode.Checkout.Session>

const fetchCheckoutSession = async ({ checkoutSessionId }: { checkoutSessionId: string }) => {
    const checkoutSession = await runCloudFunction<TFetchCheckoutSession>('fetchCheckoutSession', {
        checkoutSessionId,
    })

    return checkoutSession
}

const createPurchaseSeatsPaymentIntent = (params: {
    exams: TExams
    orgInstructorId: string
    saveCardForLater: boolean
    customerContactInfo?: {
        name?: string
        city?: string
        state?: string
        zip?: string
        country?: string
    }
}) => {
    return runCloudFunction<TCreatePurchaseSeatsPaymentIntent>('createPurchaseSeatsPaymentIntent', params)
}

/** ACTIVATE SEATS **/
type TActivateSeats = (params: {
    exams: {
        [examGuid: string]: number
    }
    paymentIntentId: string
    organizationId: string
    orgInstructorId: string
}) => Study.Class.License[]

const activateSeats = (params: {
    paymentIntentId: string
    organizationId: string
    exams: TExams
    studentsMissingLicenses: {
        [examGuid: string]: string[]
    }
    orgInstructorId: string
}) => {
    return runCloudFunction<TActivateSeats>('activateSeats', params)
}

/** FETCH RECEIPTS **/
type TFetchReceipts = (params: {
    stripeCustomerId: string
}) => StripeNode.PaymentIntent[]

const fetchReceipts = async (forceFetch?: boolean) => {
    const currentOrgInstructor = instructorModule.getters.getCurrentOrgInstructor()
    if (currentOrgInstructor?.stripeCustomerId) {
        await fetchLoadable(
            stripeModule.state.receipts,
            () => runCloudFunction<TFetchReceipts>('fetchReceipts', {
                stripeCustomerId: currentOrgInstructor.stripeCustomerId as string,
            }),
            forceFetch
        )
    }
}

/** FETCH BULK DISCOUNT PRICING **/
type TFetchBulkDiscountPricing = () => {
    [numSeats: string]: number
}

const loadStripeState = async () => {
    if (import.meta.env.VUE_APP_STRIPE_PUBLISHABLE_KEY) {
        const stripeInstance = await loadStripe(import.meta.env.VUE_APP_STRIPE_PUBLISHABLE_KEY)
        if (stripeInstance) {
            stripeModule.state.stripe = stripeInstance
            return { stripeInstance }
        }
    }

    return {
        stripe: null,
    }
}

const fetchBulkDiscountPricing = () => {
    return runCloudFunction<TFetchBulkDiscountPricing>('fetchBulkDiscountPricing')
}

const loadStripeAndElements = async ({
    bulkPurchaseAmount,
    creatingPaymentField,
    saveCardForLater,
    initialCardSetup,
}: {
    bulkPurchaseAmount?: number
    creatingPaymentField: boolean
    saveCardForLater?: boolean
    initialCardSetup?: boolean
}): Promise<{ stripe: Stripe | null; elements: StripeElements | null }> => {
    if (creatingPaymentField && stripeModule.getters.getStripe() && stripeModule.getters.getElements()) {
        // when an element is mounting/creating stripe elements and we already have a created elements
        // go ahead and destroy them so that new elements can be created with any updated info
        // example upgrade/add payment side panels get opened multiple times helps with error states
        const stripeEls = stripeModule.getters.getElements()
        stripeEls?.getElement('payment')?.destroy()
        stripeEls?.getElement('address')?.destroy()
        stripeEls?.getElement('paymentRequestButton')?.destroy()
        stripeModule.state.elements = null
        stripeModule.state.stripe = null
    }

    if (!creatingPaymentField || bulkPurchaseAmount
        && !initialCardSetup
        && stripeModule.getters.getStripe()
        && stripeModule.getters.getElements()
    ) {
        // payment or adding payment workflows need the existing element that was updated
        const elements = stripeModule.getters.getElements()
        if (elements && bulkPurchaseAmount) {
            // Calling again after passing an amount
            elements.update({
                mode: 'payment',
                amount: bulkPurchaseAmount * 100, // Stripe needs cents
                ...(saveCardForLater) && { setup_future_usage: 'on_session' },
            })
        }
        return {
            stripe: stripeModule.getters.getStripe(),
            elements: stripeModule.getters.getElements(),
        }
    }

    if (import.meta.env.VUE_APP_STRIPE_PUBLISHABLE_KEY) {
        const stripeInstance = await loadStripe(
            import.meta.env.VUE_APP_STRIPE_PUBLISHABLE_KEY, { apiVersion: '2024-06-20' })
        if (stripeInstance) {
            stripeModule.state.stripe = stripeInstance
            const elements = stripeInstance.elements({
                mode: initialCardSetup ? 'setup' : 'payment',
                // Will be overwritten later
                ...(!initialCardSetup) && { amount: 100 },
                currency: 'usd',
            })
            stripeModule.state.elements = elements

            return {
                stripe: stripeInstance,
                elements,
            }
        }
    }
    return {
        stripe: null,
        elements: null,
    }
}

type TFetchPaymentMethods = (params: {
    orgInstructorId?: string
}) => {
    defaultPaymentMethod: string | false | StripeNode.PaymentMethod | null | undefined
    paymentMethods: StripeNode.PaymentMethod[]
}

const fetchPaymentMethods = async (forceFetch?: boolean) => {
    const currentOrgInstructor = instructorModule.getters.getCurrentOrgInstructor()
    if (currentOrgInstructor) {
        await fetchLoadable(
            stripeModule.state.payment,
            () => runCloudFunction<TFetchPaymentMethods>('fetchPaymentMethods',
                { orgInstructorId: currentOrgInstructor.objectId }
            ),
            forceFetch
        )
    }
}

type TCreateSetupIntent = (params: {
    orgInstructorId?: string
}) => Promise<string | null>

const createSetupIntent = async () => {
    const currentOrgInstructor = instructorModule.getters.getCurrentOrgInstructor()
    const stripeClientSecret =
        await runCloudFunction<TCreateSetupIntent>('createSetupIntent', {
            orgInstructorId: currentOrgInstructor?.objectId,
        })
    return stripeClientSecret
}

const loadNewPaymentMethods = async () => {
    resetLoadable(stripeModule.state.payment)
    await Promise.all([
        stripeModule.actions.fetchPaymentMethods(),
    ])
}

type TDeletePaymentMethod = (params: {
    paymentMethodId: string
    orgInstructorId?: string
}) => StripeNode.PaymentMethod

const deletePaymentMethod = async (paymentMethodId: string) => {
    const currentOrgInstructor = instructorModule.getters.getCurrentOrgInstructor()
    await runCloudFunction<TDeletePaymentMethod>('deletePaymentMethod',
        { paymentMethodId, orgInstructorId: currentOrgInstructor?.objectId })

    await stripeModule.actions.fetchPaymentMethods(true)
}

export default {
    createPurchaseSeatsPaymentIntent,
    createCheckoutSession,
    fetchCheckoutSession,
    activateSeats,
    fetchReceipts,
    loadStripeAndElements,
    fetchPaymentMethods,
    createSetupIntent,
    loadStripeState,
    loadNewPaymentMethods,
    fetchBulkDiscountPricing,
    deletePaymentMethod,
}
